import { EventChannel, eventChannel } from 'redux-saga';
import {
  call,
  delay,
  race,
  select,
  take,
  takeLatest,
  cancel,
  fork,
  put,
} from 'redux-saga/effects';
import { LOGGED_IN } from '../modules/auth/actions';
import { WebSocketMessage, WebSocketMessageType } from '../models/WebSocket';
import {
  HubConnection,
  HubConnectionBuilder,
  HttpTransportType,
  LogLevel,
} from '@microsoft/signalr';
import { getAccessToken } from '../store/authEffect';
import * as ConfigValues from '../util/configValues';
import { PRIO } from '../constants';
import { isLoggedIn } from '../apps/main/rootReducer';
import { SAGA_REBUILD } from '../util/sagas';
import { tabLeaderService } from '../services/TabLeaderService';

// Import handlers (unverändert)
import { handleContactMessage } from '../modules/contacts/ws';
import { handleEmailMeMessage, handleEmailMessage } from '../modules/mail/ws';
import { handleNotificationMessage } from '../modules/notifications/ws';
import {
  handleDocumentDriveItemChangedMessage,
  handleDocumentFromTemplateCreatedMessage,
  handleDocumentMessage,
} from '../modules/documents/ws';
import {
  handleInternalProjectContactMessage,
  handleProjectExtensionAccessUpdateMessage,
} from '../modules/projects/ws';
import { handleToastMessages } from './handleToastMessages';
import handleDriveFavoriteMessage from '../modules/documents/ws/handleDriveFavoriteMessage';
import { handleContactCompanyChangedMessage } from './ws';
import { handleEmailCommentMessage } from '../modules/mail/ws/commentMessage';
import { handleEmailCopyProgressMessage } from '../modules/mail/ws/handleEmailCopyProgressMessage';
import { handleHighestErrorLevelNotificationMessage } from '../modules/zendesk/ws';
import { WINDOW_SLEEP_MANAGER } from '../services/WindowSleepManager';

// ============================================================================
// KONSTANTEN - Zentral und übersichtlich
// ============================================================================

const CONFIG = {
  WS_BC: 'prio-websocket-messages',
  WS_CONTROL_BC: 'prio-websocket-control',
  WS_DEBUG: ConfigValues.REACT_APP_WS_DEBUG === 'true',
  WS_DISABLED: ConfigValues.REACT_APP_WS_DISABLED === 'true',

  PING_INTERVAL: 15000,
  PING_TIMEOUT: 25000,
  MAX_PING_FAILURES: 3,

  RECONNECT_DELAY: 3000,
  MAX_RECONNECT_DELAY: 300000,
  RECONNECT_BACKOFF: 1.5,

  TOKEN_CHECK_INTERVAL: 45000,
  TOKEN_EXPIRY_BUFFER: 120000,

  BATCH_SIZE: 50,
  BATCH_FLUSH_INTERVAL: 100,

  // Backend requirement: Force reconnect every 30 minutes
  MAX_CONNECTION_LIFETIME: 30 * 60 * 1000, // 30 minutes
} as const;

const WS_RENEW = PRIO + 'WS_RENEW';

const SERVER_EVENTS: WebSocketMessageType[] = [
  'ProjectExtensionAccessStateUpdated',
  'ContactCompanyChangedMessage',
  'NotificationMessage',
  'EmailMeMessage',
  'EmailMessage',
  'EmailComment',
  'DocumentMessage',
  'DocumentFromTemplateCreatedMessage',
  'DocumentDriveItemChangedMessage',
  'DriveFavoriteMessage',
  'ToastMessage',
  'InternalProjectContactMessage',
  'EmailCopyProgressMessage',
  'HighestErrorLevelNotificationMessage',
];

// ============================================================================
// CONNECTION STATE - Gekapselt in eine Klasse
// ============================================================================

class ConnectionManager {
  private connection: HubConnection | null = null;
  private reconnectAttempt = 0;
  private pingFailures = 0;
  private isConnecting = false;
  private connectionStartTime = 0;

  get isConnected(): boolean {
    return this.connection?.state === 'Connected';
  }

  get currentConnection(): HubConnection | null {
    return this.connection;
  }

  get connectionAge(): number {
    return this.connectionStartTime ? Date.now() - this.connectionStartTime : 0;
  }

  setConnection(conn: HubConnection | null) {
    this.connection = conn;
    if (conn) {
      this.reconnectAttempt = 0;
      this.pingFailures = 0;
      this.connectionStartTime = Date.now();
    } else {
      this.connectionStartTime = 0;
    }
  }

  recordPingFailure(): boolean {
    this.pingFailures++;
    return this.pingFailures >= CONFIG.MAX_PING_FAILURES;
  }

  resetPingFailures() {
    this.pingFailures = 0;
  }

  getReconnectDelay(): number {
    const delay = Math.min(
      CONFIG.RECONNECT_DELAY *
        Math.pow(CONFIG.RECONNECT_BACKOFF, this.reconnectAttempt),
      CONFIG.MAX_RECONNECT_DELAY
    );
    this.reconnectAttempt++;
    return delay;
  }

  resetReconnectAttempt() {
    this.reconnectAttempt = 0;
  }

  async disconnect() {
    if (this.connection) {
      try {
        await this.connection.stop();
      } catch (e) {
        console.error('[WS] Error during disconnect:', e);
      }
      this.connection = null;
      this.connectionStartTime = 0;
    }
  }

  setConnecting(value: boolean) {
    this.isConnecting = value;
  }

  get connecting(): boolean {
    return this.isConnecting;
  }
}

const connectionManager = new ConnectionManager();

// ============================================================================
// UTILITIES
// ============================================================================

function debug(message: string, ...args: any[]) {
  if (CONFIG.WS_DEBUG) {
    console.log(`[WS] ${message}`, ...args);
  }
}

// ============================================================================
// CONNECTION SETUP - Vereinfacht
// ============================================================================

function* buildConnection() {
  const token = yield call(getAccessToken);
  const url = `${ConfigValues.REACT_APP_API_WS_URL}?access_token=${token}`;

  const connection = new HubConnectionBuilder()
    .withUrl(url, {
      transport: HttpTransportType.WebSockets,
      skipNegotiation: true,
    })
    .withAutomaticReconnect({
      nextRetryDelayInMilliseconds: () => null, // Wir managen reconnects selbst
    })
    .configureLogging(CONFIG.WS_DEBUG ? LogLevel.Debug : LogLevel.Warning)
    .build();

  connection.onreconnected(() => {
    debug('Reconnected to WebSocket server');
    WINDOW_SLEEP_MANAGER.handleLastTimeAwake(5 * 60 * 1000); // 5 minutes threshold
  });

  return connection;
}

function* connectWithRetry(): Generator<any, HubConnection | null, any> {
  if (connectionManager.connecting) {
    debug('Connection attempt already in progress');
    return null;
  }

  connectionManager.setConnecting(true);

  try {
    const connection = yield call(buildConnection);

    yield call([connection, connection.start]);

    debug('Connected successfully');
    connectionManager.resetReconnectAttempt();
    return connection;
  } catch (error) {
    console.error('[WS] Connection failed:', error);

    const retryDelay = connectionManager.getReconnectDelay();
    debug(
      `Reconnecting in ${retryDelay}ms (attempt ${connectionManager['reconnectAttempt']})`
    );

    yield delay(retryDelay);
    return null;
  } finally {
    connectionManager.setConnecting(false);
  }
}

// ============================================================================
// MESSAGE CHANNEL - Vereinfacht
// ============================================================================

function createMessageChannel(
  connection: HubConnection
): EventChannel<WebSocketMessage> {
  return eventChannel<WebSocketMessage>((emit) => {
    const handlers = SERVER_EVENTS.map((eventName) => {
      // SignalR sendet die Parameter einzeln, nicht als Objekt
      const handler = (
        type: any,
        objectType: any,
        object: any,
        projectId: any,
        destinationProjectId: any
      ) => {
        const message: WebSocketMessage = {
          source: eventName,
          type,
          objectType,
          object,
          projectId,
          destinationProjectId,
        };
        emit(message);
      };

      connection.on(eventName, handler);
      return { eventName, handler };
    });

    return () => {
      handlers.forEach(({ eventName, handler }) => {
        connection.off(eventName, handler);
      });
    };
  });
}

// ============================================================================
// BROADCAST - Vereinfacht
// ============================================================================

function createBroadcastChannel(): BroadcastChannel {
  return new BroadcastChannel(CONFIG.WS_BC);
}

function broadcastMessage(bc: BroadcastChannel, message: WebSocketMessage) {
  try {
    bc.postMessage(message);
  } catch (error) {
    console.error('[WS] Broadcast failed:', error);
  }
}

function* listenToBroadcast() {
  const channel = eventChannel<WebSocketMessage>((emit) => {
    const bc = new BroadcastChannel(CONFIG.WS_BC);
    const handler = (ev: MessageEvent) => emit(ev.data);
    bc.addEventListener('message', handler);

    return () => {
      bc.removeEventListener('message', handler);
      bc.close();
    };
  });

  try {
    while (true) {
      const message = yield take(channel);
      yield call(handleMessage, message);
    }
  } finally {
    channel.close();
  }
}

// ============================================================================
// PING - Drastisch vereinfacht
// ============================================================================

function* pingLoop(connection: HubConnection, store: any) {
  while (true) {
    yield delay(CONFIG.PING_INTERVAL);

    if (!tabLeaderService.isLeaderTab() || !connectionManager.isConnected) {
      break;
    }

    try {
      const { timeout } = yield race({
        result: call([connection, connection.invoke], 'Ping'),
        timeout: delay(CONFIG.PING_TIMEOUT),
      });

      if (timeout) {
        console.warn('[WS] Ping timeout');
        const shouldReconnect = connectionManager.recordPingFailure();

        if (shouldReconnect) {
          console.error('[WS] Too many ping failures, reconnecting');
          store.dispatch({ type: WS_RENEW });
          break;
        }
      } else {
        connectionManager.resetPingFailures();
        WINDOW_SLEEP_MANAGER.handleLastTimeAwake();
        debug('Ping successful');
      }
    } catch (error) {
      console.error('[WS] Ping error:', error);
      const shouldReconnect = connectionManager.recordPingFailure();

      if (shouldReconnect) {
        store.dispatch({ type: WS_RENEW });
        break;
      }
    }
  }
}

// ============================================================================
// TOKEN REFRESH - Vereinfacht
// ============================================================================

function* tokenRefreshLoop(store: any) {
  while (true) {
    yield delay(CONFIG.TOKEN_CHECK_INTERVAL);

    if (!tabLeaderService.isLeaderTab()) {
      break;
    }

    try {
      yield call(getAccessToken);

      // Einfache Expiry-Check (wenn Token Parsing nötig)
      // Falls Token bald abläuft -> reconnect
      // TODO: Implement actual token expiry check if needed
    } catch (error) {
      console.error('[WS] Token refresh check failed:', error);
      store.dispatch({ type: WS_RENEW });
      break;
    }
  }
}

// ============================================================================
// MESSAGE PROCESSING - Batching bleibt, aber vereinfacht
// ============================================================================

function* processMessages(
  channel: EventChannel<WebSocketMessage>,
  broadcast: BroadcastChannel
) {
  const buffer: WebSocketMessage[] = [];

  try {
    while (true) {
      if (!tabLeaderService.isLeaderTab()) {
        // Leadership verloren - broadcast remaining messages
        while (buffer.length > 0) {
          const msg = buffer.shift()!;
          broadcastMessage(broadcast, msg);
        }
        break;
      }

      const { msg, flush } = yield race({
        msg: take(channel),
        flush: delay(CONFIG.BATCH_FLUSH_INTERVAL),
      });

      if (msg) {
        buffer.push(msg);
      }

      // Flush wenn: timeout, buffer voll, oder leadership lost
      if (flush || buffer.length >= CONFIG.BATCH_SIZE) {
        while (buffer.length > 0) {
          const batch = buffer.splice(0, CONFIG.BATCH_SIZE);

          for (const message of batch) {
            broadcastMessage(broadcast, message);
            yield call(handleMessage, message);
          }
        }
      }
    }
  } finally {
    // Final flush
    while (buffer.length > 0) {
      const message = buffer.shift()!;
      broadcastMessage(broadcast, message);
      yield call(handleMessage, message);
    }
  }
}

// ============================================================================
// MESSAGE HANDLER - Unverändert
// ============================================================================

function* handleMessage(data: WebSocketMessage) {
  try {
    debug('Handling message:', data.source);

    switch (data.source) {
      case 'ProjectExtensionAccessStateUpdated':
        yield handleProjectExtensionAccessUpdateMessage(data);
        break;
      case 'ContactMessage':
        yield handleContactMessage(data);
        break;
      case 'ContactCompanyChangedMessage':
        yield handleContactCompanyChangedMessage(data);
        break;
      case 'NotificationMessage':
        yield handleNotificationMessage(data);
        break;
      case 'EmailMeMessage':
        yield handleEmailMeMessage(data);
        break;
      case 'EmailMessage':
        yield handleEmailMessage(data);
        break;
      case 'EmailComment':
        yield handleEmailCommentMessage(data);
        break;
      case 'DocumentMessage':
        yield handleDocumentMessage(data);
        break;
      case 'DocumentFromTemplateCreatedMessage':
        yield handleDocumentFromTemplateCreatedMessage(data);
        break;
      case 'DocumentDriveItemChangedMessage':
        yield handleDocumentDriveItemChangedMessage(data);
        break;
      case 'InternalProjectContactMessage':
        yield handleInternalProjectContactMessage(data);
        break;
      case 'ToastMessage':
        yield handleToastMessages(data);
        break;
      case 'DriveFavoriteMessage':
        yield call(handleDriveFavoriteMessage, data);
        break;
      case 'EmailCopyProgressMessage':
        yield handleEmailCopyProgressMessage(data);
        break;
      case 'HighestErrorLevelNotificationMessage':
        yield handleHighestErrorLevelNotificationMessage(data);
        break;
      default:
        console.warn('[WS] Unhandled message type:', data.source);
    }
  } catch (error) {
    console.error('[WS] Error handling message:', error, data);
  }
}

// ============================================================================
// MAIN CONNECTION LOOP - Mit 30-Minuten-Reconnect
// ============================================================================

function* runAsLeader(store: any) {
  debug('Running as leader tab');

  const broadcast = createBroadcastChannel();
  let connection: HubConnection | null = null;
  let pingTask = null;
  let tokenTask = null;
  let processingTask = null;

  try {
    while (true) {
      // Check if we're still leader
      if (!tabLeaderService.isLeaderTab()) {
        debug('Leadership lost, stopping');
        break;
      }

      // Connect with retry
      connection = yield call(connectWithRetry);

      if (!connection) {
        // Retry failed, loop continues
        WINDOW_SLEEP_MANAGER.handleLastTimeAwake(0); // Force immediate check
        continue;
      }

      connectionManager.setConnection(connection);

      debug('Connection established, starting loops');

      // Setup message channel
      const messageChannel = createMessageChannel(connection);

      // Setup disconnect channel - capture connection in local scope
      const currentConnection = connection;
      const disconnectChannel = eventChannel<boolean>((emit) => {
        const handler = () => emit(true);
        currentConnection.onclose(handler);
        return () => {};
      });

      // Start background tasks
      pingTask = yield fork(pingLoop, connection, store);
      tokenTask = yield fork(tokenRefreshLoop, store);
      processingTask = yield fork(processMessages, messageChannel, broadcast);

      // Wait for disconnect event, renew action, OR max connection lifetime
      const { disconnected, renew, maxLifetime } = yield race({
        disconnected: take(disconnectChannel),
        renew: take(WS_RENEW),
        // Backend requirement: Force reconnect after 30 minutes
        maxLifetime: delay(CONFIG.MAX_CONNECTION_LIFETIME),
      });

      // Cancel background tasks
      if (pingTask) yield cancel(pingTask);
      if (tokenTask) yield cancel(tokenTask);
      if (processingTask) yield cancel(processingTask);

      // Cleanup
      disconnectChannel.close();
      messageChannel.close();
      yield call([connectionManager, connectionManager.disconnect]);

      if (maxLifetime) {
        console.info(
          '[WS] Max connection lifetime reached (30 min), performing scheduled reconnect'
        );
        yield delay(1000); // Short delay before reconnect
      } else if (disconnected) {
        console.warn('[WS] Connection closed unexpectedly');
        yield delay(CONFIG.RECONNECT_DELAY);
      } else if (renew) {
        debug('Renew requested');
        yield delay(1000); // Short delay before reconnect
      }
    }
  } finally {
    // Final cleanup
    if (pingTask) yield cancel(pingTask);
    if (tokenTask) yield cancel(tokenTask);
    if (processingTask) yield cancel(processingTask);
    yield call([connectionManager, connectionManager.disconnect]);
    broadcast.close();
    debug('Leader cleanup complete');
  }
}

function* watchLeadershipChange() {
  const channel = eventChannel<{
    isLeader: boolean;
    leaderId: string | null;
    epoch: number;
  }>((emit) => {
    const unsubscribe = tabLeaderService.onLeadershipChange(
      (isLeader, leaderId, epoch) => {
        emit({ isLeader, leaderId, epoch });
      }
    );
    return unsubscribe;
  });

  try {
    while (true) {
      const { isLeader, leaderId, epoch } = yield take(channel);

      debug('Leadership changed:', { isLeader, leaderId, epoch });

      if (isLeader) {
        yield put({ type: WS_RENEW });
      }
    }
  } finally {
    channel.close();
  }
}

// ============================================================================
// ENTRY POINT - Vereinfacht
// ============================================================================

function* connectWebSocket(store: any) {
  yield delay(500); // Initial delay

  const loggedIn = yield select(isLoggedIn);

  if (!loggedIn || CONFIG.WS_DISABLED) {
    return;
  }

  if (tabLeaderService.isLeaderTab()) {
    yield call(runAsLeader, store);
  } else {
    yield call(listenToBroadcast);
  }
}

// ============================================================================
// MAIN SAGA EXPORT
// ============================================================================

export default function* webSocketFlow(store: any) {
  yield fork(watchLeadershipChange);
  yield takeLatest(
    [LOGGED_IN, WS_RENEW, SAGA_REBUILD],
    connectWebSocket,
    store
  );
}
